import clsx from 'clsx';
import * as React from 'react';

import { ButtonGroup, type IconDefinition, renderIcon, Tooltip } from '@gitbook/spine-react';

import styles from './TableOfContents.module.css';

/**
 * View for an entry in the TOC.
 */
export const ToCMenuItem = React.forwardRef(function TOCMenuItemWithRef(
    props: {
        leftIcon?: IconDefinition;
        icon?: IconDefinition;
        leftIconTooltip?: string;
        rightIcons?: Array<{
            iconComponent: IconDefinition;
            iconTooltip?: string;
            iconTestId?: string;
        }>;
        kind?: 'group' | 'primary' | 'secondary';
        children: React.ReactNode;
        buttons?: React.ReactNode;
        textStyle?: string;
        leftIconStyle?: string;
        disabled?: boolean;
        active?: boolean;
        onPress?: (e: React.MouseEvent) => void;
        href?: string;
        dimmed?: boolean;
        className?: string;
    },
    ref: React.Ref<HTMLDivElement>
): React.ReactElement {
    const {
        icon,
        leftIconTooltip,
        leftIcon,
        leftIconStyle,
        rightIcons = [],
        kind = 'primary',
        children,
        buttons,
        active = false,
        onPress,
        disabled,
        href,
        dimmed = false,
        className,
    } = props;

    const interactiveProps = onPress
        ? href
            ? ({ href } as const)
            : ({ tabIndex: 0, disabled } as const)
        : {};

    const Component = onPress ? (href ? 'a' : 'button') : 'div';

    const textStyle = [props.textStyle, dimmed ? 'text-light' : null];

    const leftIconElement = leftIcon ? (
        <div className="m-nudge-icon mr-4 flex shrink-0">
            {renderIcon(leftIcon, {
                size: 14,
                className: clsx(textStyle, leftIconStyle),
            })}
        </div>
    ) : null;
    return (
        <Component
            className={clsx('w-full', styles.tocMenuItem, className)}
            {...interactiveProps}
            {...(active ? { 'aria-current': 'page' } : {})}
            aria-disabled={disabled}
            onClick={(e) => {
                if (disabled) {
                    return;
                }
                onPress?.(e);
            }}
        >
            <div
                ref={ref}
                className={clsx(
                    'flex items-center px-2 py-1',
                    kind === 'group' && 'text-base',
                    active ? 'font-normal' : null
                )}
            >
                {leftIconElement ? (
                    leftIconTooltip ? (
                        <Tooltip label={leftIconTooltip} disabled={!leftIconTooltip}>
                            {leftIconElement}
                        </Tooltip>
                    ) : (
                        leftIconElement
                    )
                ) : null}
                {icon ? (
                    <div className="mr-3 flex shrink-0 items-center">
                        {renderIcon(icon, {
                            size: 14,
                            className: clsx(textStyle),
                        })}
                    </div>
                ) : null}
                <div
                    className={clsx(
                        'flex size-full items-center',
                        // Min height to ensure the item stays consistent in height with or without a button
                        'min-h-7',
                        textStyle,
                        kind === 'group' ? 'text-ui-heading-group uppercase' : null
                    )}
                >
                    {children}
                </div>
                {rightIcons.length > 0 ? (
                    <div className="mr-2 ml-4 flex items-center gap-2">
                        {rightIcons.map((rightIcon, index) => {
                            const rightIconElement = (
                                <div
                                    key={`toc-menu-item-${index}`}
                                    className={clsx('m-nudge-icon flex shrink-0', textStyle)}
                                    data-testid={rightIcon.iconTestId}
                                >
                                    {renderIcon(rightIcon.iconComponent, {
                                        size: 14,
                                        className: 'text-light',
                                    })}
                                </div>
                            );

                            return rightIcon.iconTooltip ? (
                                <Tooltip
                                    key={`toc-menu-item-tooltip-${index}`}
                                    label={rightIcon.iconTooltip}
                                    disabled={!rightIcon.iconTooltip}
                                >
                                    {rightIconElement}
                                </Tooltip>
                            ) : (
                                rightIconElement
                            );
                        })}
                    </div>
                ) : null}
                {buttons ? (
                    <ButtonGroup className="max-h-6 shrink-0 items-center">{buttons}</ButtonGroup>
                ) : null}
            </div>
        </Component>
    );
});
